<template>
  <div class="overall-details-page">
    <b-row>
      <b-col
        lg="4"
        class="mb-2 mb-lg-0"
      >
        <b-card class="details-card">
          <div class="logo-wrap">
            <b-avatar
              size="88"
              :src="company.logo || ''"
              class="company-logo"
            />
          </div>

          <p class="section-title">
            {{ detailsLabel }}
          </p>

          <div class="detail-lines">
            <p><strong>Company Name:</strong> {{ company.name || '-' }}</p>
            <p><strong>Address:</strong> {{ company.address || '-' }}</p>
            <p><strong>Zip code:</strong> {{ company.pincode || '-' }}</p>
            <p><strong>City:</strong> {{ company.city || '-' }}</p>
            <p><strong>Followers:</strong> {{ company.followers || 0 }}</p>
            <p><strong>Running Ads:</strong> {{ company.running_ads || 0 }}</p>
            <p><strong>Contact Person Name:</strong> {{ company.contact_person || '-' }}</p>
            <p><strong>Contact :</strong> {{ company.contact_number || '-' }}</p>
            <p><strong>Reported Users :</strong> {{ company.reported_users || 0 }}</p>
            <p><strong>Password :</strong> ********</p>
          </div>

          <div class="card-actions">
            <b-button
              variant="light"
              class="suspend-btn"
              :disabled="suspendingType !== null"
              @click="suspendVendor('temporary')"
            >
              {{ suspendingType === 'temporary' ? 'Processing...' : 'Temporary Suspend' }}
              <b-spinner
                v-if="suspendingType === 'temporary'"
                small
                class="ml-50"
                label="Processing"
              />
            </b-button>
            <b-button
              variant="light"
              class="suspend-btn"
              :disabled="suspendingType !== null"
              @click="suspendVendor('permanent')"
            >
              {{ suspendingType === 'permanent' ? 'Processing...' : 'Suspend' }}
              <b-spinner
                v-if="suspendingType === 'permanent'"
                small
                class="ml-50"
                label="Processing"
              />
            </b-button>
          </div>
        </b-card>
      </b-col>

      <b-col lg="8">
        <b-card class="chart-card">
          <b-tabs
            v-model="activeTab"
            pills
            class="overview-tabs"
          >
            <b-tab title="Overall Details">
              <div class="chart-head">
                <b-form-select
                  v-model="timeRange"
                  :options="timeOptions"
                  class="range-select"
                />
                <div class="legend">
                  <span><em class="legend-dot dot-blue" />120 Ads</span>
                  <span><em class="legend-dot dot-red" />50 Followers</span>
                  <span><em class="legend-dot dot-dark" />8K Clicks</span>
                </div>
              </div>

              <vue-apex-charts
                type="line"
                height="300"
                :series="series"
                :options="chartOptions"
              />
            </b-tab>

            <b-tab title="Uploaded Ads">
              <div class="tab-toolbar">
                <b-form-input
                  v-model="uploadedSearch"
                  class="toolbar-input"
                  placeholder="Search"
                />
                <b-form-select
                  v-model="uploadedFilter"
                  :options="uploadedFilterOptions"
                  class="toolbar-select"
                />
              </div>

              <div class="uploaded-grid">
                <div
                  v-for="item in filteredUploadedAds"
                  :key="item.id"
                  class="uploaded-card cursor-pointer"
                  role="button"
                  tabindex="0"
                  @click="openAdDetail(item)"
                  @keydown.enter.prevent="openAdDetail(item)"
                >
                  <img
                    :src="item.image"
                    alt="Ad"
                    class="uploaded-image"
                  >

                  <span
                    class="ad-state"
                    :class="`status-${item.statusKey}`"
                  >
                    {{ item.status }}
                  </span>

                  <div class="uploaded-content">
                    <div class="uploaded-head">
                      <h5>{{ item.title || 'Ad Title' }}</h5>
                      <small>{{ item.createdAt || '-' }}</small>
                    </div>
                    <p>{{ item.likes }} Likes - {{ item.dislikes }} Dislike - {{ item.reports }} Reports</p>
                    <div class="uploaded-foot">
                      <span>{{ item.views }} views - {{ item.shares }} Share - {{ item.saved }} Saved</span>
                      <div class="delete-btn">
                        <feather-icon
                          v-if="!deletingUploadedAdIds.includes(item.id)"
                          icon="Trash2Icon"
                          size="14"
                          class="cursor-pointer"
                          :class="{ 'text-muted': deletingUploadedAdIds.includes(item.id) }"
                          @click.stop="deleteUploadedAd(item)"
                        />
                        <b-spinner
                          v-else
                          small
                          label="Deleting"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </b-tab>

            <b-tab title="Report">
              <div class="tab-toolbar">
                <b-form-input
                  v-model="reportSearch"
                  class="toolbar-input"
                  placeholder="Search"
                />
                <b-form-select
                  v-model="reportFilter"
                  :options="reportFilterOptions"
                  class="toolbar-select"
                />
              </div>

              <b-table
                responsive
                :items="filteredReportRows"
                :fields="reportFields"
                class="report-table mb-0"
              >
                <template #cell(reportedAd)="data">
                  <div class="reported-ad-cell">
                    <div class="mini-poda">
                      <span>P</span>
                      <span>O</span>
                      <span>D</span>
                      <span>A</span>
                    </div>
                    <span>{{ data.item.reportedAd || '-' }}</span>
                  </div>
                </template>

                <template #cell(reportedBy)="data">
                  <div class="reported-by-cell">
                    <b-avatar
                      size="28"
                      :src="data.item.reportedByAvatar || ''"
                    />
                    <span>{{ data.item.reportedBy || '-' }}</span>
                  </div>
                </template>

                <template #cell(reason)="data">
                  <small>{{ data.item.reason || '-' }}</small>
                </template>

                <template #cell(actions)="data">
                  <div class="report-actions">
                    <feather-icon
                      icon="MailIcon"
                      size="15"
                      class="cursor-pointer"
                      @click="contactReporter(data.item)"
                    />
                    <feather-icon
                      icon="Trash2Icon"
                      size="15"
                      class="cursor-pointer"
                      @click="removeReportRow(data.item)"
                    />
                  </div>
                </template>
              </b-table>

              <div class="report-footer">
                <small>Showing 1 to 8 of 100 entries</small>
                <b-pagination
                  v-model="reportPage"
                  :total-rows="100"
                  :per-page="8"
                  size="sm"
                  first-number
                  last-number
                  class="mb-0"
                >
                  <template #prev-text>
                    Previous
                  </template>
                  <template #next-text>
                    Next
                  </template>
                </b-pagination>
              </div>
            </b-tab>
          </b-tabs>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BTabs,
  BTab,
  BButton,
  BAvatar,
  BFormSelect,
  BFormInput,
  BTable,
  BPagination,
  BSpinner,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import { vendorPanelApi } from '@/services/vendorPanelApi'
import { notifyInfo, confirmAction } from '@/utils/uiActions'
import { extractApiErrorMessage, normalizeAdStatusKey } from '@/utils/adCreate'
import { resolveMediaUrl } from '@/utils/mediaUrl'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BTabs,
    BTab,
    BButton,
    BAvatar,
    BFormSelect,
    BFormInput,
    BTable,
    BPagination,
    BSpinner,
    VueApexCharts,
  },
  data() {
    return {
      activeTab: 0,
      uploadedSearch: '',
      uploadedFilter: 'all',
      reportSearch: '',
      reportFilter: 'all',
      reportPage: 1,
      uploadedFilterOptions: [
        { value: 'all', text: 'Select' },
        { value: 'active', text: 'Active' },
        { value: 'expired', text: 'Expired' },
        { value: 'pending', text: 'Pending' },
        { value: 'pending_approval', text: 'Pending approval' },
      ],
      reportFilterOptions: [
        { value: 'all', text: 'All' },
        { value: 'pending', text: 'Pending' },
        { value: 'resolved', text: 'Resolved' },
      ],
      uploadedAds: [],
      reportFields: [
        { key: 'reportId', label: 'REPORT ID' },
        { key: 'reportedAd', label: 'REPORTED AD' },
        { key: 'reportedBy', label: 'REPORTED BY' },
        { key: 'reason', label: 'REASON' },
        { key: 'date', label: 'DATE' },
        { key: 'actions', label: 'ACTIONS' },
      ],
      reportRows: [],
      company: {},
      timeRange: 'Last 24 Day`s',
      timeOptions: ['Last 24 Day`s', 'Last 7 Day`s', 'Last 30 Day`s'],
      series: [
        { name: 'Ads', data: [4, 8, 5, 7, 8, 4, 5, 7, 5, 6, 3, 8] },
        { name: 'Followers', data: [1, 4, 2, 3, 4, 1, 4, 5, 2, 1, 2, 1] },
        { name: 'Clicks', data: [3, 6, 1, 4, 6, 2, 3, 9, 3, 4, 1, 5] },
      ],
      chartOptions: {
        chart: {
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        dataLabels: { enabled: false },
        stroke: { curve: 'straight', width: 2 },
        colors: ['#5f6fdc', '#e7445b', '#2b2b38'],
        xaxis: {
          categories: ['2 Days', '4 Days', '6 Days', '8 Days', '10 Days', '12 Days', '14 Days', '16 Days', '18 Days', '20 Days', '22 Days', '24 Days'],
          labels: {
            style: { colors: '#a3a0b5', fontSize: '11px' },
          },
        },
        yaxis: {
          min: 0,
          max: 9,
          tickAmount: 9,
          labels: {
            style: { colors: '#a3a0b5', fontSize: '11px' },
          },
        },
        grid: {
          borderColor: '#ecebf3',
          strokeDashArray: 0,
        },
        legend: { show: false },
      },
      suspendingType: null,
      deletingUploadedAdIds: [],
    }
  },
  mounted() {
    this.fetchOverallData()
  },
  computed: {
    detailsLabel() {
      return this.activeTab === 2 ? 'Advertisers Details' : 'Details'
    },
    filteredUploadedAds() {
      const term = this.uploadedSearch.trim().toLowerCase()
      return this.uploadedAds.filter(item => {
        const byFilter = this.uploadedFilter === 'all' || item.statusKey === this.uploadedFilter
        const bySearch = !term || `${item.title} ${item.status}`.toLowerCase().includes(term)
        return byFilter && bySearch
      })
    },
    filteredReportRows() {
      const term = this.reportSearch.trim().toLowerCase()
      return this.reportRows.filter(item => {
        const byFilter = this.reportFilter === 'all'
          || String(item.status || '').toLowerCase() === this.reportFilter
        const bySearch = !term || `${item.reportId} ${item.reportedAd || ''} ${item.reportedBy || ''} ${item.reason || ''}`.toLowerCase().includes(term)
        return byFilter && bySearch
      })
    },
  },
  methods: {
    async fetchOverallData() {
      try {
        const [overallRes, uploadedRes, reportsRes] = await Promise.all([
          vendorPanelApi.overallDetails({ range: 'last_24_days' }),
          vendorPanelApi.uploadedAds({ page: 1, per_page: 6, search: '', status: 'all' }),
          vendorPanelApi.adsReports({ page: 1, per_page: 6, search: '', filter: 'all' }),
        ])
        const overallData = overallRes?.data?.data || {}
        this.company = overallData.company || {}
        const graph = overallData.graph || []
        this.series = [
          { name: 'Ads', data: graph.map(item => item.ads || 0) },
          { name: 'Followers', data: graph.map(item => item.followers || 0) },
          { name: 'Clicks', data: graph.map(item => item.clicks || 0) },
        ]
        this.chartOptions = {
          ...this.chartOptions,
          xaxis: { ...this.chartOptions.xaxis, categories: graph.map(item => item.day || '') },
        }

        const uploaded = uploadedRes?.data?.data?.ads || []
        this.uploadedAds = uploaded.map(item => ({
          id: item.id,
          status: item.status || 'active',
          statusKey: normalizeAdStatusKey(item.status || 'active'),
          image: resolveMediaUrl(item.thumbnail || item.thambnail || item.image),
          title: item.title || 'Ad Title',
          createdAt: item.created_at || '',
          likes: item.likes || 0,
          dislikes: item.dislikes || 0,
          reports: item.reports || 0,
          views: item.views || 0,
          shares: item.shares || 0,
          saved: item.saved || 0,
        }))

        const reportList = reportsRes?.data?.data?.reports || []
        this.reportRows = reportList.map(item => ({
          reportId: item.report_id,
          date: item.date,
          reportedAd: item.ad?.title,
          reportedBy: item.reported_by?.name,
          reportedByAvatar: item.reported_by?.avatar || '',
          reason: (item.reason || []).join(', '),
          status: String(item.status || '').toLowerCase(),
        }))
      } catch (error) {
        this.company = {}
        this.uploadedAds = []
        this.reportRows = []
      }
    },
    async suspendVendor(type) {
      if (this.suspendingType) return
      this.suspendingType = type
      try {
        await vendorPanelApi.suspend({ type })
        this.$swal({
          title: 'Updated',
          text: `Vendor ${type} suspend applied`,
          icon: 'success',
          timer: 1700,
          showConfirmButton: false,
        })
      } catch (error) {
        this.$swal({
          title: 'Error',
          text: 'Unable to suspend vendor',
          icon: 'error',
          timer: 2200,
          showConfirmButton: false,
        })
      }
      this.suspendingType = null
    },
    deleteUploadedAd(item) {
      if (this.deletingUploadedAdIds.includes(item.id)) return
      confirmAction(
        this,
        {
          title: 'Delete ad?',
          text: 'This action cannot be undone.',
          confirmButtonText: 'Delete',
        },
        async () => {
          this.deletingUploadedAdIds.push(item.id)
          try {
            await vendorPanelApi.deleteAd({ ad_id: item.id })
            this.fetchOverallData()
            this.$swal({
              title: 'Deleted',
              text: 'Uploaded ad deleted successfully',
              icon: 'success',
              timer: 1600,
              showConfirmButton: false,
            })
          } catch (error) {
            this.$swal({
              title: 'Error',
              text: extractApiErrorMessage(error, 'Unable to delete uploaded ad'),
              icon: 'error',
              timer: 2200,
              showConfirmButton: false,
            })
          } finally {
            this.deletingUploadedAdIds = this.deletingUploadedAdIds.filter(id => id !== item.id)
          }
        },
      )
    },
    openAdDetail(item) {
      if (!item?.id) return
      this.$router.push({ name: 'my-ads-detail', params: { id: String(item.id) } })
    },
    contactReporter() {
      notifyInfo(this, 'Reporter contact will be available when messaging is connected.', 'Contact')
    },
    removeReportRow(item) {
      this.reportRows = this.reportRows.filter(row => row !== item)
      notifyInfo(this, 'Report entry removed from the list.', 'Removed')
    },
  },
}
</script>

<style scoped lang="scss">
.overall-details-page {
  .details-card,
  .chart-card {
    border: 1px solid #ecebf3;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(45, 50, 78, 0.07);
  }

  .chart-card {
    min-height: 100%;
  }

  .logo-wrap {
    display: flex;
    justify-content: center;
    margin-bottom: 1rem;
  }

  .company-logo {
    border: 3px solid #f0eff5;
  }

  .section-title {
    color: #a39fb4;
    margin-bottom: 0.9rem;
  }

  .detail-lines {
    p {
      margin-bottom: 0.45rem;
      color: #5f5a73;
      font-size: 0.95rem;
    }
  }

  .card-actions {
    margin-top: 1rem;
    display: flex;
    gap: 0.7rem;
  }

  .suspend-btn {
    background: #ffe8ea;
    color: #d26a75;
    border-color: #ffe8ea;
    font-weight: 600;
  }

  .overview-tabs {
    ::v-deep .nav-pills .nav-link {
      color: #8e89a3;
      font-weight: 600;
      padding: 0.5rem 0.95rem;
    }

    ::v-deep .nav-pills .nav-link.active {
      background: #011425;
      color: #fff;
    }

    ::v-deep .tab-content {
      padding-top: 0.85rem;
    }
  }

  .chart-head,
  .tab-toolbar {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
    flex-wrap: wrap;
  }

  .chart-head {
    justify-content: space-between;
  }

  .range-select {
    max-width: 180px;
  }

  .toolbar-input {
    width: 180px;
  }

  .toolbar-select {
    width: 120px;
  }

  .legend {
    display: flex;
    align-items: center;
    gap: 1.2rem;
    flex-wrap: wrap;
    color: #47415c;
    font-weight: 600;
  }

  .legend-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    display: inline-block;
    margin-right: 0.35rem;
  }

  .dot-blue { background: #5f6fdc; }
  .dot-red { background: #e7445b; }
  .dot-dark { background: #2b2b38; }

  .uploaded-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.75rem;
  }

  .uploaded-card {
    border-radius: 8px;
    overflow: hidden;
    background: #181d2d;
    position: relative;
  }

  .uploaded-image {
    width: 100%;
    height: 135px;
    object-fit: cover;
    display: block;
  }

  .ad-state {
    position: absolute;
    top: 7px;
    right: 7px;
    font-size: 0.62rem;
    font-weight: 700;
    border-radius: 10px;
    padding: 0.12rem 0.45rem;
    color: #fff;
  }

  .status-active { background: #00b341; }
  .status-expired { background: #f44336; }
  .status-pending,
  .status-pending_approval { background: #7367f0; }
  .status-rejected { background: #ea5455; }
  .status-inactive,
  .status-stopped,
  .status-draft { background: #82868b; }

  .uploaded-content {
    color: #fff;
    padding: 0.52rem 0.6rem 0.45rem;

    h5 {
      color: #fff;
      margin: 0;
      font-size: 1rem;
      font-weight: 600;
    }

    p,
    span,
    small {
      color: #b7b9c6;
      font-size: 0.67rem;
      margin: 0;
    }
  }

  .uploaded-head,
  .uploaded-foot {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.5rem;
  }

  .delete-btn {
    width: 22px;
    height: 22px;
    border-radius: 4px;
    background: #fb1d2c;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
  }

  .report-table {
    ::v-deep thead th {
      color: #7a758f;
      font-size: 0.76rem;
      font-weight: 700;
      border-top: 1px solid #ecebf3;
    }

    ::v-deep tbody td {
      color: #5e5873;
      font-size: 0.9rem;
      vertical-align: middle;
    }
  }

  .reported-ad-cell,
  .reported-by-cell {
    display: flex;
    align-items: center;
    gap: 0.45rem;
  }

  .mini-poda {
    width: 36px;
    height: 36px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr 1fr;
    border-radius: 4px;
    overflow: hidden;

    span {
      display: flex;
      align-items: center;
      justify-content: center;
      background: #011425;
      color: #fff;
      font-size: 0.52rem;
      border: 1px solid rgba(255, 255, 255, 0.12);
    }
  }

  .report-actions {
    display: flex;
    gap: 0.6rem;
    color: #6f6b86;
  }

  .report-footer {
    padding: 0.85rem 0.25rem 0.1rem;
  }

  @media (max-width: 991px) {
    .uploaded-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: 640px) {
    .uploaded-grid {
      grid-template-columns: 1fr;
    }
  }
}
</style>
